import { useState, useEffect } from 'react';
import { tokenAdminService, AdminTokenTransaction } from '../../services/token-admin.service';

export function TokenTransactionsPage() {
  const [transactions, setTransactions] = useState<AdminTokenTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadTransactions();
  }, [filterType, filterStatus]);

  const loadTransactions = async () => {
    setIsLoading(true);
    setError('');
    try {
      const filters: any = {};
      if (filterType !== 'all') filters.type = filterType;
      if (filterStatus !== 'all') filters.status = filterStatus;
      
      const data = await tokenAdminService.getTransactions(filters);
      setTransactions(data);
    } catch (err: any) {
      setError(err.message || 'Error al cargar transacciones');
    } finally {
      setIsLoading(false);
    }
  };

  const getTypeBadge = (type: string) => {
    const badges: Record<string, { class: string; label: string }> = {
      purchase: { class: 'bg-blue-100 text-blue-700', label: 'Compra' },
      usage: { class: 'bg-yellow-100 text-yellow-700', label: 'Uso' },
      refund: { class: 'bg-red-100 text-red-700', label: 'Reembolso' },
      bonus: { class: 'bg-green-100 text-green-700', label: 'Bonus' },
    };
    return badges[type] || { class: 'bg-gray-100 text-gray-700', label: type };
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { class: string; label: string }> = {
      completed: { class: 'bg-green-100 text-green-700', label: 'Completado' },
      pending: { class: 'bg-yellow-100 text-yellow-700', label: 'Pendiente' },
      failed: { class: 'bg-red-100 text-red-700', label: 'Fallido' },
      cancelled: { class: 'bg-gray-100 text-gray-500', label: 'Cancelado' },
    };
    return badges[status] || { class: 'bg-gray-100 text-gray-700', label: status };
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredTransactions = transactions.filter(tx => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      tx.userName?.toLowerCase().includes(term) ||
      tx.userEmail?.toLowerCase().includes(term) ||
      tx.description?.toLowerCase().includes(term) ||
      tx.id?.toLowerCase().includes(term)
    );
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando transacciones...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold">Transacciones de Tokens</h1>
          <p className="text-gray-600">Historial completo de transacciones del sistema</p>
        </div>
        <button onClick={loadTransactions} className="btn btn-secondary">🔄 Actualizar</button>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">❌</span>
          <span>{error}</span>
        </div>
      )}

      {/* Filtros */}
      <div className="flex flex-wrap gap-4 items-center">
        <input
          type="text"
          placeholder="Buscar por usuario, email, descripción..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 min-w-[200px] px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        />
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          <option value="all">Todos los tipos</option>
          <option value="purchase">Compras</option>
          <option value="usage">Usos</option>
          <option value="refund">Reembolsos</option>
          <option value="bonus">Bonus</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          <option value="all">Todos los estados</option>
          <option value="completed">Completados</option>
          <option value="pending">Pendientes</option>
          <option value="failed">Fallidos</option>
          <option value="cancelled">Cancelados</option>
        </select>
      </div>

      {/* Tabla */}
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Usuario</th>
              <th>Tipo</th>
              <th>Monto</th>
              <th>Descripción</th>
              <th>Estado</th>
              <th>Fecha</th>
            </tr>
          </thead>
          <tbody>
            {filteredTransactions.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-8 text-gray-500">
                  No hay transacciones que coincidan con los filtros
                </td>
              </tr>
            ) : (
              filteredTransactions.map((tx) => {
                const typeBadge = getTypeBadge(tx.type);
                const statusBadge = getStatusBadge(tx.status);
                return (
                  <tr key={tx.id}>
                    <td className="font-mono text-xs">{tx.id.slice(0, 8)}...</td>
                    <td>
                      <div>
                        <p className="font-medium">{tx.userName || 'Usuario'}</p>
                        <p className="text-xs text-gray-500">{tx.userEmail}</p>
                      </div>
                    </td>
                    <td>
                      <span className={`text-xs px-2 py-1 rounded-full ${typeBadge.class}`}>
                        {typeBadge.label}
                      </span>
                    </td>
                    <td className={`font-bold ${tx.amount > 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {tx.amount > 0 ? '+' : ''}{tx.amount} tokens
                    </td>
                    <td className="max-w-xs truncate">{tx.description}</td>
                    <td>
                      <span className={`text-xs px-2 py-1 rounded-full ${statusBadge.class}`}>
                        {statusBadge.label}
                      </span>
                    </td>
                    <td className="text-sm text-gray-500">{formatDate(tx.createdAt)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}